/**
 * Session State should be used for any saved global state, having it all
 * in one place greatly reduces session state issues.
 */
import { create as createStore } from "zustand"
import { persist } from "zustand/middleware"
import { dayjs } from "@seamapi/shared/dayjs"

interface SessionState {
  isAuthenticated: boolean
  sessionToken?: string

  betaOn: boolean

  // TODO this will probably be in the useMe hook in the future
  timeZoneConfig: {
    timeZoneAbbr: string // e.g. PDT, EDT
    timeZoneName: string // "America/Los_Angeles"
  }

  setBetaOn: (betaOn: boolean) => any

  setIsAuthenticated: ((isAuthenticated: true, sessionToken?: string) => any) &
    ((isAuthenticated: false) => any)

  setTimeZone: (timeZoneName: string) => void
}

export const useSessionState = createStore(
  persist<SessionState>(
    (set) => {
      const timeZoneName = dayjs.tz.guess()
      dayjs.tz.setDefault(timeZoneName)
      return {
        isAuthenticated: false,

        betaOn: false,

        timeZoneConfig: {
          timeZoneAbbr: dayjs().tz(timeZoneName).format("z"),
          timeZoneName,
        },

        setBetaOn: (betaOn) => {
          set((state) => ({
            ...state,
            betaOn,
          }))
        },

        setIsAuthenticated: (isAuthenticated, sessionToken?: string) => {
          set((state) => ({
            ...state,
            isAuthenticated,
            sessionToken,
          }))
        },

        setTimeZone: (timeZoneName) => {
          set((state) => ({
            ...state,
            timeZoneConfig: {
              timeZoneName,
              timeZoneAbbr: dayjs().tz(timeZoneName).format("z"),
            },
          }))
        },
      }
    },
    {
      name: "session-state",
      version: 2,
      migrate: (persistedState: any) => {
        if (!persistedState) return persistedState

        const persistedTimeZoneName =
          persistedState?.timeZoneConfig?.timeZoneName ?? dayjs.tz.guess()
        const nextTimeZoneAbbr = dayjs().tz(persistedTimeZoneName).format("z")

        return {
          ...persistedState,
          timeZoneConfig: {
            timeZoneName: persistedTimeZoneName,
            timeZoneAbbr: nextTimeZoneAbbr,
          },
        }
      },
    }
  )
)

useSessionState.subscribe(
  (state) => state.timeZoneConfig.timeZoneName,
  // @ts-expect-error because the second argument is deprecated / removed
  (timeZoneName) => {
    dayjs.tz.setDefault(timeZoneName)
  }
)
